import { BucketId } from '../types/File';
import { constructPublicUrl } from '../utils/urlUtils';

const abortError = () => Object.assign(new Error('Upload cancelled'), { name: 'AbortError' });

export class DirectUploadService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Uploads a file by streaming it to the same-origin `/api/upload` endpoint (a Pages
   * Function), which writes it straight into R2. Progress and cancellation work via XHR.
   *
   * @param file - The file to upload
   * @param bucket - Which bucket to upload to
   * @param onProgress - Optional callback for upload progress (0-100)
   * @param signal - Optional AbortSignal to cancel the upload
   * @returns Promise with the file key and public URL
   */
  async uploadLargeFile(
    file: File,
    bucket: BucketId,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<{ key: string; url: string }> {
    const contentType = file.type || 'application/octet-stream';
    const params = new URLSearchParams({ bucket, filename: file.name });

    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }

      const xhr = new XMLHttpRequest();
      const onAbort = () => xhr.abort();
      signal?.addEventListener('abort', onAbort);
      const cleanup = () => signal?.removeEventListener('abort', onAbort);

      xhr.open('POST', `${this.baseUrl}/upload?${params.toString()}`, true);
      xhr.setRequestHeader('Content-Type', contentType);

      xhr.onload = function () {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) {
            onProgress(100);
          }
          try {
            const data = JSON.parse(xhr.responseText);
            const key = data?.file?.key ?? data?.key;
            if (!key) {
              reject(new Error('Upload response missing file key'));
              return;
            }
            resolve({ key, url: constructPublicUrl(key, bucket) });
          } catch {
            reject(new Error('Invalid upload response'));
          }
        } else {
          let message = `Failed to upload: ${xhr.status}`;
          try {
            const data = JSON.parse(xhr.responseText);
            if (data?.error) {
              message = data.error;
            }
          } catch {
            // keep the default message
          }
          reject(new Error(message));
        }
      };

      xhr.onerror = function () {
        cleanup();
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = function () {
        cleanup();
        reject(abortError());
      };

      xhr.upload.onprogress = function (event) {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.send(file);
    });
  }
}
